import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSaveNoSlotInfomationMutation } from '../../api/queries/topslot';
import { SaveNoSlotParams } from '../../interfaces/AvailableNoSlots';
import { NoTopSlot } from '../../interfaces/NoTopSlot';
import { sendAnalyticsNoSlots } from '../../services/Analytics';
import Alert from '../Alert/Alert';
import Button from '../Button';
import { Separator } from '../Separator/Separator';
import { StepsUpdateContext } from '../Steps/StepsContext';
import style from './NoSlotForm.module.scss';
import NoSlotSelectors from './NoSlotSelectors';

interface NoSlotFormProps {
  hashId: string;
  noTopSlots: NoTopSlot[];
  workorderId: string;
}

const NoSlotForm: FC<NoSlotFormProps> = ({ noTopSlots, hashId, workorderId }) => {
  const { completeCurrentStep, navigateToNextStep } = useContext(StepsUpdateContext);
  const { saveNoSlotInformation, isPending, isError, reset } = useSaveNoSlotInfomationMutation(hashId, workorderId);
  const [noTopSlotsUpdated, setNoTopSlotsUpdated] = useState<NoTopSlot[]>(noTopSlots);
  const { t } = useTranslation();

  const selectedNoTopSlots = useMemo<NoTopSlot[]>(() => noTopSlotsUpdated
    .filter((noToSlotItem: NoTopSlot) => {
      return noToSlotItem.periods.some((period) => period.selected);
    })
    .map(slot => ({
      ...slot,
      periods: slot.periods.filter(period => period.selected)
    })), [noTopSlotsUpdated]);

  const selectedPeriodCount = useMemo<number>(() => selectedNoTopSlots.reduce((count: number, notTopSlotItem: NoTopSlot) => {
    return count + notTopSlotItem.periods.filter((period) => period.selected).length;
  }, 0), [selectedNoTopSlots]);

  const onComplete = useCallback(() => {
    completeCurrentStep();
    navigateToNextStep({ state: { selectedNoTopSlots } });
  }, [completeCurrentStep, navigateToNextStep, selectedNoTopSlots]);

  const saveNoTopslots = useCallback(async (): Promise<void> => {
    const params: SaveNoSlotParams = {
      version: 'new',
      noSlots: selectedNoTopSlots,
    };

    await saveNoSlotInformation(params);
    sendAnalyticsNoSlots(params);
    onComplete();
  }, [selectedNoTopSlots, onComplete, saveNoSlotInformation]);

  const handleSlotSelection = useCallback(async (selectedNoTopSlots: NoTopSlot[]): Promise<void> => {
    setNoTopSlotsUpdated(selectedNoTopSlots);
    if (isError) {
      reset();
    }
  }, [setNoTopSlotsUpdated, isError, reset]);

  return (
    <div>
      <NoSlotSelectors noTopSlots={noTopSlotsUpdated} updateSlots={handleSlotSelection} />
      <div className={style.extraInfoRoot}>
        <div>
          {!!isError && (
            <div className={style.errorMessage} >
              <Alert
                level="error"
                text={t('alert:somethingWentWrong')}
              />
            </div>
          )}
        </div>
      </div>
      <div className={style.seperator}>
        <Separator />
      </div>
      <div className={style.btnsContainer}>
        <div className={style.btnsSection}>
          <Button
            disabled={isPending || selectedPeriodCount < 3}
            onClick={saveNoTopslots}
            size="full"
            text={t('noslots:scheduleAutoBtn')}
          />
        </div>
      </div>
    </div>
  );
};

export default NoSlotForm;
